import { useMountedState } from '@flippingbook/web.react-hooks/dist/useMountedState';
import type { ServiceError } from '@flippingbook/web.utils/dist/infrastructure-interfaces/ServiceError';
import { WellKnownError } from '@flippingbook/web.utils/dist/infrastructure-interfaces/WellKnownError';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { createSearchParams, useSearchParams } from 'react-router-dom';

import { loc } from '@/localization';
import { optionsService } from '@/services/instance/optionsService';
import { sso } from '@/services/instance/sso';
import { getLoginUrl } from '@/utils/getPageUrl';
import { email, password } from '@/utils/validationRules';

const validationRules = {
    email,
    newPassword: password,
    confirmPassword: password,
} as const;

interface FormDataType {
    email: string;
}

export function useRestoreStartPage() {
    const [successEmail, setSuccessEmail] = useState('');

    const isMounted = useMountedState();

    const [searchParams] = useSearchParams();

    const initialEmail = searchParams.get('email') || undefined;

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        getValues,
        watch,
        trigger: triggerValidation,
        formState: { errors, isSubmitting },
    } = useForm<FormDataType>({
        defaultValues: {
            email: initialEmail,
        },
    });

    const maybeEmail = watch('email');

    const loginUrl = getLoginUrl({
        ...optionsService.getQueryArgs(),
        email: maybeEmail,
    });

    const cleanErrors = useCallback(() => {
        clearErrors('email');
    }, [clearErrors]);

    const sendRequest = useCallback((email: string) => {
        const host = window.location.host;
        const appId = optionsService.getAppId();

        const searchParams = createSearchParams({
            ...optionsService.getQueryArgs(),
            r: appId !== 'mars' ? appId : 'site', // mars should redirect to site restore form
            email,
        }).toString();

        const design = optionsService.getDesign();
        const redirectArgs = `${design ? `/${design}` : ''}?${searchParams}`;

        return sso.requestResetPassword({
            email,
            loginUrl: `https://${host}/login${redirectArgs}`,
            returnUrl: `https://${host}/restore-password-update${redirectArgs}`,
            failedUrl: `https://${host}/restore-password-failed${redirectArgs}`,
            cancelUrl: `https://${host}/restore-password-cancelled${redirectArgs}`,
        });
    }, []);

    const submitForm = useCallback(
        async (data: FormDataType) => {
            const formValid = await triggerValidation();
            if (!formValid) {
                return;
            }

            try {
                await sendRequest(data.email);

                if (isMounted()) {
                    setSuccessEmail(data.email);
                }
            } catch (error) {
                if (isMounted()) {
                    const notFindError = (error as { data: { Errors?: ServiceError[] } })?.data?.Errors?.find(
                        (err) => err?.WellKnownError === WellKnownError.UserNotFound,
                    );

                    if (notFindError) {
                        setError('email', {
                            type: 'value',
                            message: loc.Login.RestorePasswordEmailNotFound,
                        });
                        return;
                    }
                }

                throw error;
            }
        },
        [isMounted, sendRequest, setError, triggerValidation],
    );

    const onFormSubmit = useCallback(
        (ev: React.BaseSyntheticEvent) => {
            void handleSubmit(submitForm)(ev);
        },
        [handleSubmit, submitForm],
    );

    return {
        loginUrl,
        successEmail,
        register,
        errors,
        cleanErrors,
        onFormSubmit,
        isSubmitting,
        getValues,
        validationRules,
    };
}
